/**
 * TurnstileScene — the contained visual for the air-gapped / sovereign band.
 *
 * A direct TENET reference: two opposed particle streams braid through a central
 * "turnstile" aperture. Amber runs forward, cyan runs inverted; they counter-
 * rotate as they pass through the gate. Stands in for entropy reversal — and,
 * here, for the boundary an air-gapped system enforces: data crosses the gate
 * only on your terms. Decorative; pointer events stay off.
 */

import { useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";

const LEN = 9; // corridor length along Z

interface P {
  r: number;
  angle: number;
  speed: number;
  offset: number;
}

/** One directional stream of particles spiralling through the gate. */
function Stream({ dir, color, count = 520 }: { dir: 1 | -1; color: string; count?: number }) {
  const ref = useRef<THREE.Points>(null);
  const data = useMemo<P[]>(
    () =>
      Array.from({ length: count }, () => ({
        r: 0.22 + Math.random() * 1.05,
        angle: Math.random() * Math.PI * 2,
        speed: 0.5 + Math.random() * 0.9,
        offset: Math.random() * LEN,
      })),
    [count],
  );
  const positions = useMemo(() => new Float32Array(count * 3), [count]);

  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    for (let i = 0; i < count; i++) {
      const p = data[i];
      // Wrap z into [-LEN/2, LEN/2], travelling in the stream's direction.
      let z = ((t * p.speed * dir + p.offset) % LEN + LEN) % LEN - LEN / 2;
      // Swirl tightens near the gate (z≈0); direction sets the rotation sense.
      const swirl = p.angle + (1 / (0.35 + Math.abs(z))) * 0.9 * dir + t * 0.15 * dir;
      positions[i * 3] = Math.cos(swirl) * p.r;
      positions[i * 3 + 1] = Math.sin(swirl) * p.r;
      positions[i * 3 + 2] = z;
    }
    if (ref.current) ref.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial
        color={color}
        size={0.045}
        sizeAttenuation
        transparent
        opacity={0.85}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        toneMapped={false}
      />
    </points>
  );
}

/** The gate: an aperture ring + a slow wireframe core being inverted. */
function Gate() {
  const core = useRef<THREE.Mesh>(null);
  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    if (core.current) {
      core.current.rotation.x = t * 0.4;
      core.current.rotation.y = t * 0.25;
    }
  });
  return (
    <group>
      {/* aperture ring (hole faces ±Z) */}
      <mesh>
        <torusGeometry args={[1.25, 0.012, 12, 120]} />
        <meshBasicMaterial color="#8896af" transparent opacity={0.5} toneMapped={false} />
      </mesh>
      {/* inner glow ring */}
      <mesh>
        <torusGeometry args={[1.25, 0.05, 8, 120]} />
        <meshBasicMaterial color="#f4600f" transparent opacity={0.12} toneMapped={false} />
      </mesh>
      {/* the object crossing the turnstile */}
      <mesh ref={core}>
        <octahedronGeometry args={[0.42, 0]} />
        <meshBasicMaterial color="#3fc8f0" wireframe transparent opacity={0.6} toneMapped={false} />
      </mesh>
    </group>
  );
}

export function TurnstileScene({ className }: { className?: string }) {
  return (
    <Canvas
      className={className}
      camera={{ position: [3.4, 1.3, 4.2], fov: 42 }}
      dpr={[1, 2]}
      gl={{ antialias: true, alpha: true }}
    >
      <fog attach="fog" args={["#070b14", 4.5, 12]} />
      <Gate />
      <Stream dir={1} color="#ff8a42" />
      <Stream dir={-1} color="#3fc8f0" />
    </Canvas>
  );
}
